import { React, useState, useEffect, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { StatCard, DateRangePicker } from '../dashboard';
import { PillTabs } from '../../';
import {
    AdminDashboard as AdminDashboardApi,
    AdminAnalytics as AdminAnalyticsApi,
    AdminModerationStats as AdminModerationStatsApi,
} from '../../../api';

const DASH_TABS = [
    { value: 'users', label: 'Users' },
    { value: 'content', label: 'Content' },
    { value: 'moderation', label: 'Moderation' },
];

function LoiseDashboard() {
    const [tab, setTab] = useState('users');
    const [hours, setHours] = useState(24);
    const [today, setToday] = useState({ loading: false });
    const [analytics, setAnalytics] = useState({ loading: false, rows: [] });
    const [modStats, setModStats] = useState({ loading: false, rows: [] });

    const days = Math.max(1, Math.ceil(hours / 24));

    const refresh = useCallback(() => {
        AdminDashboardApi(setToday);
        AdminAnalyticsApi(setAnalytics, days, 'loise');
        AdminModerationStatsApi(setModStats, days);
    }, [days]);

    // Keyed on hours, not days: sub-day presets (1h..24h) all map to days=1 but should still refetch.
    useEffect(() => {
        refresh();
    }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps

    // The server returns 2 x period days: the previous period first, then the current one.
    const modAll = modStats.rows.map((r) => ({ ...r, label: r.date?.slice(5) }));
    const modRows = modAll.slice(modAll.length / 2);
    const modPrev = modAll.slice(0, modAll.length / 2);
    const rows = analytics.rows.map((r) => ({ ...r, label: r.date?.slice(5) }));
    const loading = today.loading || analytics.loading;

    const pct = (curr, prev) => {
        if (curr == null || prev == null) return null;
        if (prev === 0) return curr > 0 ? 100 : null;
        return ((curr - prev) / prev) * 100;
    };

    // Range totals per moderation kind, with censored count + rate in the sub-line.
    const modCard = (kind, label) => {
        const sum = (key) => modRows.reduce((a, r) => a + (r[`${kind}_${key}`] ?? 0), 0);
        const prevMod = modPrev.reduce((a, r) => a + (r[`${kind}_mod`] ?? 0), 0);
        const mod = sum('mod');
        const cens = sum('cens');
        const rate = mod ? ((cens / mod) * 100).toFixed(1) : '0.0';
        return (
            <StatCard
                label={label}
                value={mod}
                change={pct(mod, prevMod)}
                loading={modStats.loading}
                sub={`${cens} censored · ${rate}%`}
                sparkline={modRows}
                sparklineKey={`${kind}_mod`}
            />
        );
    };

    return (
        <Box sx={{ m: 2, height: 'calc(100% - 32px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <PillTabs value={tab} onChange={setTab} tabs={DASH_TABS} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size='small' onClick={refresh} disabled={loading}>
                        <RefreshIcon fontSize='small' />
                    </IconButton>
                    <DateRangePicker hours={hours} onChange={setHours} />
                </Box>
            </Box>

            {tab === 'users' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                    <StatCard
                        label='new-users'
                        value={today.new_users}
                        loading={today.loading}
                        sparkline={rows}
                        sparklineKey='new_users'
                        change={pct(today.new_users, today.prev_new_users)}
                    />
                    <StatCard
                        label='active-users'
                        value={today.active_users}
                        loading={today.loading}
                        accent
                        sparkline={rows}
                        sparklineKey='active_users'
                        change={pct(today.active_users, today.prev_active_users)}
                    />
                </Box>
            )}

            {tab === 'content' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                    <StatCard
                        label='new-posts'
                        value={today.new_posts}
                        loading={today.loading}
                        sparkline={rows}
                        sparklineKey='new_posts'
                        change={pct(today.new_posts, today.prev_new_posts)}
                    />
                    <StatCard
                        label='new-messages'
                        value={today.new_messages}
                        loading={today.loading}
                        sparkline={rows}
                        sparklineKey='new_messages'
                        change={pct(today.new_messages, today.prev_new_messages)}
                    />
                    <StatCard
                        label='new-likes'
                        value={today.new_likes}
                        loading={today.loading}
                        sparkline={rows}
                        sparklineKey='new_likes'
                        change={pct(today.new_likes, today.prev_new_likes)}
                    />
                    <StatCard
                        label='new-comments'
                        value={today.new_comments}
                        loading={today.loading}
                        sparkline={rows}
                        sparklineKey='new_comments'
                        change={pct(today.new_comments, today.prev_new_comments)}
                    />
                    <StatCard
                        label='new-reports'
                        value={today.new_reports}
                        loading={today.loading}
                        sparkline={rows}
                        sparklineKey='new_reports'
                        change={pct(today.new_reports, today.prev_new_reports)}
                    />
                </Box>
            )}

            {tab === 'moderation' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                    {modCard('image', 'post-image')}
                    {modCard('movie', 'post-video')}
                    {modCard('desc', 'post-desc')}
                    {modCard('comment', 'comment')}
                    {modCard('pic', 'profile-pic')}
                </Box>
            )}
        </Box>
    );
}

export { LoiseDashboard };
